<template>
  <div v-if="rightsLoaded">
    <!-- Row top buttons -->
    <div class="row q-mb-lg">
      <div class="col-6" style="justify-content: flex-start">
        <q-btn
          class="q-mr-md capitalize ctrl-button"
          icon="keyboard_backspace"
          color="primary capitalize"
          :label="$store.state.stringsStore.strings.action.toOverview"
          align="left"
          @click="$router.push('/usergroups')"
        />
      </div>
      <div class="col-6">
        <q-btn
          class="q-ml-md capitalize ctrl-button right"
          icon-right="save"
          color="primary capitalize"
          :label="$store.state.stringsStore.strings.action.save"
          :disable="dataInvalid"
          :class="{'disabled-btn': dataInvalid}"
          align="right"
          @click="addUsergroup"
        />
      </div>
    </div>

    <!-- Columns -->
    <div class="row gutter-sm q-mb-lg">

      <!-- Left column -->
      <div class="col-md-6 col-sm-12 col-xs-12 q-pa-sm">

        <!-- Usergroup block -->
        <div class="row q-mb-sm">
          <div class="full-width">
            <p class="card-title"
              v-text="$store.state.stringsStore.strings.module.usergroups.single">
            </p>
            <div class="card-block scroll">
              <q-field class="q-mb-md"
                :error-label="$store.state.stringsStore.strings.module.usergroups.details.error.name">
                <q-input
                  type="text"
                  v-model.trim="$v.usergroup.name.$model"
                  :float-label="$store.state.stringsStore.strings.name"
                  @input="$v.usergroup.name.$touch"
                  :error="$v.usergroup.name.$error ? true : false">
                </q-input>
              </q-field>
            </div>
          </div>
        </div>

        <!-- Rights block -->
        <div class="row q-mb-sm">
          <div class="full-width">
            <p class="card-title"
              v-text="$store.state.stringsStore.strings.module.usergroups.details.rights.label">
            </p>
            <div class="card-block no-padding scroll" v-if="rightsLoaded">
              <template v-for="(module, i) in rights">
                <q-list :key="i" class="custom-q-list accordion">
                  <q-collapsible
                    :duration="50" :icon="mapIcon(module.icon)" :label="getString(module.label)">
                    <div class="row">
                      <div class="col-6 q-pa-md q-pb-md"
                        :key="i" v-for="(submodule, i) in module.modules">
                        <p class="text-weight-bold" v-text="getString(submodule.label)"></p>
                        <div :key="i" v-for="(right, i) in submodule.rights">
                          <q-checkbox :val="right.code" v-model="usergroup.aRights"
                            :label="getString(right.label)"
                          />
                        </div>
                      </div>
                    </div>
                  </q-collapsible>
                </q-list>
              </template>
            </div>
          </div>
        </div>

      </div>

      <!-- Right column -->
      <div class="col-md-6 col-sm-12 col-xs-12">

        <!-- Search users block -->
        <div class="row q-mb-xs">
          <div class="full-width">
            <p class="card-title">
              {{ $store.state.stringsStore.strings.module.users.multiple }} ({{numberOfUsers}})
            </p>

            <div class="q-mb-md">
              <div class="row">
                <!-- Searchfield -->
                <div class="q-mr-md" style="flex-grow: 1">
                  <q-input
                    color="grey-5"
                    v-model="searchTerm"
                    @input="applyFilters"
                    :placeholder="$store.state.stringsStore.strings
                    .module.usergroups.details.members.list.filter.label"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Users block -->
        <div class="row q-mb-sm">
          <!-- Data table -->
          <div class="q-table" id="myTable">
            <vuetable
              ref="vuetable"
              class="table-block q-mb-md"
              :class="{ 'is-loading': isLoading }"
              :table-height="tableHeight"
              :api-url="'user'"
              pagination-path
              :api-mode="true"
              :noDataTemplate="noDataString(`${tableContext}`)"
              :css="css.table"
              :http-fetch="fetchData"
              :fields="columns"
              :row-class="selectable ? applyRowClass : null"
              @vuetable:loading="onLoading"
              @vuetable:load-success="onSuccess"
              @vuetable:load-error="onError"
              @vuetable:pagination-data="onPaginationData">
              <!-- Custom fields -->
              <!-- User slot -->
              <div slot="name"
                class="user-slot"
                slot-scope="props">
                <!-- User avatar -->
                <img v-if="!fallbacks.includes(props.rowData.id)" :src="userAvatar(props.rowData.avatar)" @error="fallbackImage(props.rowData.id)" class="table-gravatar">
                <img v-else :src="`${base_url}default-avatar.jpeg`" class="table-gravatar">
                <!-- User full name -->
                <div class="user-name">
                  <div
                    v-html="highlight(fullname(props.rowData.name,
                      props.rowData.active), filters.name)">
                  </div>
                </div>
              </div>

              <div slot="member" slot-scope="props">
                <q-toggle
                  v-model="usergroup.aMembers" :val="props.rowData.id" color="secondary"/>
              </div>
            </vuetable>
            <!-- Pagination -->
            <div class="row pagination-bottom" id="myPagination">
              <!-- Per page select -->
              <div class="col">
                <div class="row">
                  <q-select
                    class="btn-compact select-small"
                    hide-underline
                    color="grey"
                    v-model="perPage"
                    :options="perPageOptions"
                  />
                </div>
              </div>
              <div class="col right-margin">
                <vuetable-pagination
                  :on-each-side="1"
                  :css="css.pagination"
                  @vuetable-pagination:change-page="onChangePage"
                  ref="pagination">
                </vuetable-pagination>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  </div>
</template>

<script>

import Vuetable from 'vuetable-2';
import VuetablePagination from 'vuetable-2/src/components/VuetablePagination.vue';

// Table columns defined in columnsDefinition file
import { required } from 'vuelidate/lib/validators';

// Mixins
import tableMixin from '@/mixins/tables/tableMixin';
import pagination from '@/mixins/tables/pagination';
import searchHighlight from '@/mixins/tables/searchHighlight';
import fetchData from '@/mixins/tables/fetchData';

// Helpers
import getString from '@/helpers/GetStrings';

export default {
  name: 'UsergroupsAdd',
  created() {
    // Fetch usergoup rights
    this.fetchRights();
  },
  mixins: [
    tableMixin,
    pagination,
    searchHighlight,
    fetchData,
  ],
  components: {
    Vuetable,
    VuetablePagination,
  },
  data() {
    return {
      env_url: process.env.VUE_APP_API_URL,
      base_url: process.env.BASE_URL,
      fallbacks: [],
      tableHeight: 'calc(100vh - 360px)',
      columns: [
        {
          name: '__slot:name',
          title: getString('name'),
          label: 'name',
          sortField: 'name',
          titleClass: 'text-left sortable vcol-l',
          dataClass: 'text-left vcol-l',
        },
        {
          name: '__slot:member',
          title: getString('member'),
          label: 'member',
          titleClass: 'text-left sortable vcol-xs',
          dataClass: 'text-left vcol-xs',
        },
      ],
      selectable: false,
      checked: false,
      data: null,
      tableContext: 'users',
      tableEndpoint: 'user',
      isLoading: false,
      filters: {
        name: '',
      },

      // To send
      usergroup: {
        name: '',
        aRights: [],
        aMembers: [],
      },

      // Var to set when all requirements are met
      dataInvalid: true,

      userNumber: 0,
      rights: {},
      rightsLoaded: false,
      icons: {
        dashboard: 'dashboard',
        usergroups: 'people',
        developer: 'code',
        library: 'library_books',
        blog: 'art_track',
        news: 'import_contacts',
        press: 'camera',
        master: 'select_all',
        error: 'error_outline',
      },
    };
  },
  validations: {
    usergroup: {
      name: { required },
    },
  },
  methods: {
    userAvatar(path) {
      return path.includes('www.gravatar.com') ? path : `${process.env.VUE_APP_API_URL}${path}`;
    },

    fallbackImage(id) {
      this.fallbacks.push(id);
    },
    getString,
    // Override mixin
    onSuccess(response) {
      // Set loading to false
      this.isLoading = false;
      // Modify the response object to fit vuetable.
      response.data.data = response.data.users;

      // For selectable tables, map row ids to an array.
      if (this.selectable) {
        this.rowData = response.data.data.map(row => row.id);
      }
    },
    // Gather UsergroupParams from data
    getUsergroupParams() {
      // Define
      const rights = [];
      const users = [];
      // Loop over clicked rights
      this.usergroup.aRights.forEach((right) => {
        rights.push(`rights[]=${right}`);
      });
      // Loop over switched mebers
      this.usergroup.aMembers.forEach((member) => {
        users.push(`users[]=${member}`);
      });
      // Return as formData
      return [
        rights.length ? rights.join('&') : null,
        users.length ? users.join('&') : null,
      ].filter(val => val !== null).join('&');
    },
    // Fire request to add usergroup
    addUsergroup() {
      // Make request and add name in the request
      this.axios.post('usergroup', `${this.getUsergroupParams()}&name=${encodeURIComponent(this.usergroup.name)}`)
        .then((response) => {
          // Throw a positive notification
          this.$q.notify({
            message: this.$store.state.stringsStore.strings.module.usergroups.details.saved,
            position: 'top-right',
            timeout: 1000,
            color: 'positive',
          });

          // Fetch new me and navigation since the users own rights may have been affected.
          this.$store.dispatch('getMe');
          this.$store.dispatch('getNavigation');

          // Check if location-header is available in reponse headers
          if (response.headers.location) {
            // Substract id from location-header in response
            const usergroupId = response.headers.location.substring(response.headers.location.lastIndexOf('/') + 1);
            // Check if usergroupId is not empty
            if (usergroupId !== '') {
              // Route to usergroup for details and editing
              this.$router.push(`/usergroups/${usergroupId}`);
            } else {
              // No expected usergroupId, Route to overview
              this.$router.push('/usergroups');
            }
          // No expected response, Route to overview
          } else {
            this.$router.push('/usergroups');
          }
        })
        .catch((error) => {
          console.warn(error);
        });
    },
    async fetchRights() {
      try {
        const response = await this.axios.get('usergroup/rights');
        if (response.data.rights) {
          this.rights = response.data.rights;
          this.rightsLoaded = true;
        }
      } catch (error) {
        console.warn(error);
      }
    },
    fullname(name, active) {
      if (active === true) {
        return `${[name.firstname, name.surname].filter(val => val !== null).join(' ')}`;
      }

      return `${[name.firstname, name.surname].filter(val => val !== null).join(' ')}  <span class="label bg-primary text-white">${this.$store.state.stringsStore.strings.inactive}</span>`;
    },
    mapIcon(icon) {
      return this.icons[icon] ? this.icons[icon] : this.icons.error;
    },
  },
  computed: {
    numberOfUsers() {
      return this.usergroup.aMembers.length;
    },
  },
  watch: {
    // Put a watch on the usergroup property
    usergroup: {
      handler(val) {
        if (val.name !== '' && !this.$v.usergroup.name.$invalid && val.aRights.length > 0 && val.aMembers.length > 0) {
          this.dataInvalid = false;
        } else {
          this.dataInvalid = true;
        }
      },
      deep: true,
    },
  },
};
</script>

<style lang="stylus" scoped>
@import '~variables'
.table-gravatar
  display:inline-block
  height 2rem
  width 2rem
  margin 0 1rem 0 0
  -webkit-background-size cover
  border-radius 50%

.user-slot
  display inline-flex
  vertical-align middle

.user-name
  line-height 2rem
</style>
